import { useTranslation } from 'react-i18next';
import { Table, ScrollArea, Button, Group } from '@mantine/core';
import moment from 'moment';
import { formatCompactNumber, formatEnUsValue, formatProjectDate, useProjectDateFormat } from '../../utils/helpers';

function BillSummaryTable({ beforeMG, afterMG, showComparison = true }) {
  const { t } = useTranslation();
  const projectDateFormat = useProjectDateFormat();
  const FIELDS = [
    { key: 'energy', label: t('result.billSummary.table.energy') },
    { key: 'demand', label: t('result.billSummary.table.demand') },
    { key: 'energyCharges', label: t('result.billSummary.table.energyCharges') },
    { key: 'energyRevenue', label: t('result.billSummary.table.energyRevenue') },
    { key: 'demandCharges', label: t('result.billSummary.table.demandCharges') },
    { key: 'fixedCharges', label: t('result.billSummary.table.fixedCharges') },
    { key: 'subscribedCharges', label: t('result.billSummary.table.subscribedCharges') },
    { key: 'penaltyCharges', label: t('result.billSummary.table.penaltyCharges') },
    { key: 'totalCharges', label: t('result.billSummary.table.totalCharges') },
  ];

  const primaryData = showComparison ? beforeMG : (afterMG || beforeMG);
  const months = primaryData?.map(bm => bm.periodStart) ?? [];
  const formatMonth = (value) => {
    if (!value) return value;
    const parsed = moment(value, [
      'YYYY-MM',
      'YYYY-MM-DD',
      'YYYY/MM/DD',
      'MM/YYYY',
      'M/YYYY',
      'DD/MM/YYYY',
      'DD/MMM/YYYY',
      'DD/MMM',
    ], true);
    return parsed.isValid() ? formatProjectDate(parsed.toDate(), projectDateFormat) : value;
  };

  const getFormattedCell = (value) => {
    if (value === null || value === undefined || value === '-') {
      return { display: '-', hover: '-' };
    }
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return { display: value, hover: value };
    }
    return {
      display: formatCompactNumber(numericValue),
      hover: formatEnUsValue(numericValue),
    };
  };

  const getFieldTotal = (rows, fieldKey) => (rows || []).reduce((acc, row) => {
    const value = Number(row?.[fieldKey]);
    return Number.isFinite(value) ? acc + value : acc;
  }, 0);
  
  // Export CSV function
  const handleExportCSV = () => {
    // CSV-escape: wrap in quotes if value contains comma, quote, or non-ASCII
    const csvEscape = (val) => {
      const s = String(val);
      if (/[,"\n\r]/.test(s) || /[^\x20-\x7E]/.test(s)) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    };
    const headers = showComparison
      ? ['Date', ...FIELDS.flatMap(field => [`${field.label} (Before)`, `${field.label} (After)`])]
      : ['Date', ...FIELDS.map(field => field.label)];
    
    const rows = months.map((date, i) => [
      date,
      ...(showComparison
        ? FIELDS.flatMap(field => [
            beforeMG?.[i]?.[field.key] ?? '-',
            afterMG?.[i]?.[field.key] ?? '-',
          ])
        : FIELDS.map(field => primaryData?.[i]?.[field.key] ?? '-')),
    ]);

    const totalsRow = showComparison
      ? [
          t('common.total') || 'Total',
          ...FIELDS.flatMap(field => [
            getFieldTotal(beforeMG, field.key),
            getFieldTotal(afterMG, field.key),
          ]),
        ]
      : [
          t('common.total') || 'Total',
          ...FIELDS.map(field => getFieldTotal(primaryData, field.key)),
        ];

    // Create CSV content
    const csvContent = [
      headers.map(csvEscape).join(','),
      ...rows.map(row => row.map(cell => csvEscape(cell)).join(',')),
      totalsRow.map(cell => csvEscape(cell)).join(','),
    ].join('\n');

    // Create blob and download with UTF-8 BOM for proper Unicode rendering (€, £, ¥, etc.)
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'bill_summary.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const tableRows = months.map((date, i) => (
    <tr key={date}>
      <td style={{minWidth: '110px'}}>{formatMonth(date)}</td>
      {showComparison
        ? FIELDS.map(field => (
            <>
              <td
                key={field.key + '-before'}
                style={{ fontWeight: 600 }}
                title={getFormattedCell(beforeMG?.[i]?.[field.key] ?? '-').hover}
              >
                {getFormattedCell(beforeMG?.[i]?.[field.key] ?? '-').display}
              </td>
              <td
                key={field.key + '-after'}
                style={{ fontWeight: 600 }}
                title={getFormattedCell(afterMG?.[i]?.[field.key] ?? '-').hover}
              >
                {getFormattedCell(afterMG?.[i]?.[field.key] ?? '-').display}
              </td>
            </>
          ))
        : FIELDS.map(field => (
            <td
              key={field.key}
              style={{ fontWeight: 600 }}
              title={getFormattedCell(primaryData?.[i]?.[field.key] ?? '-').hover}
            >
              {getFormattedCell(primaryData?.[i]?.[field.key] ?? '-').display}
            </td>
          ))}
    </tr>
  ));

  const totalLabel = t('common.total') || 'Total';
  const totalRow = (
    <tr key="total-row">
      <td style={{ fontWeight: 700 }}>{totalLabel}</td>
      {showComparison
        ? FIELDS.map(field => {
            const beforeTotal = getFieldTotal(beforeMG, field.key);
            const afterTotal = getFieldTotal(afterMG, field.key);
            return (
              <>
                <td
                  key={field.key + '-total-before'}
                  style={{ fontWeight: 700 }}
                  title={getFormattedCell(beforeTotal).hover}
                >
                  {getFormattedCell(beforeTotal).display}
                </td>
                <td
                  key={field.key + '-total-after'}
                  style={{ fontWeight: 700 }}
                  title={getFormattedCell(afterTotal).hover}
                >
                  {getFormattedCell(afterTotal).display}
                </td>
              </>
            );
          })
        : FIELDS.map(field => {
            const total = getFieldTotal(primaryData, field.key);
            return (
              <td
                key={field.key + '-total'}
                style={{ fontWeight: 700 }}
                title={getFormattedCell(total).hover}
              >
                {getFormattedCell(total).display}
              </td>
            );
          })}
    </tr>
  );

  return (
    <div>
      <Group gap="xs" style={{ marginBottom: 16 }}>
        <Button
          size="xs"
          variant="default"
          onClick={handleExportCSV}
        >
          Export CSV
        </Button>
      </Group>
    <ScrollArea>
      <Table striped highlightOnHover withColumnBorders className='indicator-downloadable'>
        <thead>
          <tr>
            <th rowSpan={showComparison ? 2 : 1}>{t('common.date')}</th>
            {FIELDS.map(field => (
              <th
                key={field.key + '-component'}
                colSpan={showComparison ? 2 : 1}
                style={{ textAlign: 'center' }}
              >
                {field.label}
              </th>
            ))}
          </tr>
          {showComparison && (
            <tr>
              {FIELDS.map(field => (
                <>
                  <th key={field.key + '-before'} style={{ color: '#1976d2', textAlign: 'center' }}>{t('result.billSummary.legend.before')}</th>
                  <th key={field.key + '-after'} style={{ color: '#388e3c', textAlign: 'center' }}>{t('result.billSummary.legend.after')}</th>
                </>
              ))}
            </tr>
          )}
        </thead>
        <tbody>
          {tableRows}
          {totalRow}
        </tbody>
      </Table>
    </ScrollArea>
   </div>
  );
}

export default BillSummaryTable;
